import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { SellerProfile, MarketplaceListing } from '@/types/residents/marketplaceListing';
import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import { 
  StarRating, 
  ListingCard, 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  StatItem,
} from '@/components/residents/marketplace';
import { 
  calculateGridItemSize,
  getResponsivePadding,
  getResponsiveGridGap,
  MARKETPLACE_MAX_CONTENT_WIDTH,
} from '@/utils/marketplace';
import { useGetSellerProfileQuery, useToggleFollowMutation } from '@/store/api';
import { useAppSelector } from '@/store';
import { selectUser } from '@/store/selectors/userSelectors';

type SellerProfileNavigationProp = StackNavigationProp<RootStackParamList, 'SellerProfile'>;
type SellerProfileRouteProp = RouteProp<RootStackParamList, 'SellerProfile'>;

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

// Default profile for loading state
const DEFAULT_PROFILE: SellerProfile = {
  id: '',
  name: '',
  initials: '',
  rating: 0,
  reviewCount: 0,
  bought: 0,
  sold: 0,
  followers: 0,
  following: 0,
  isFollowing: false,
  listings: [],
};

export const SellerProfileScreen: React.FC = () => {
  const navigation = useNavigation<SellerProfileNavigationProp>();
  const route = useRoute<SellerProfileRouteProp>();
  const { layoutMode, isWeb } = useResponsive();
  const { sellerId } = route.params;
  
  // Get current user to check if viewing own profile
  const currentUser = useAppSelector(selectUser);
  const isOwnProfile = currentUser?.accountId?.toString() === sellerId || currentUser?.id?.toString() === sellerId;
  const { navigateToListing } = useListingNavigation();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const gridGap = getResponsiveGridGap(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;

  // Fetch seller profile from API
  const { 
    data, 
    isLoading,
  } = useGetSellerProfileQuery(sellerId);

  // Use nullish coalescing to handle null responses
  const sellerProfile = data ?? DEFAULT_PROFILE;

  // Local state for optimistic follow UI
  const [isFollowing, setIsFollowing] = useState(sellerProfile.isFollowing);

  // Toggle follow mutation
  const [toggleFollow] = useToggleFollowMutation();

  // Sync local state when profile data changes
  useEffect(() => {
    setIsFollowing(sellerProfile.isFollowing);
  }, [sellerProfile.isFollowing]);

  const itemSize = calculateGridItemSize({ layoutMode });

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleFollowPress = async () => {
    const newState = !isFollowing;
    setIsFollowing(newState); // Optimistic update
    
    try {
      await toggleFollow(sellerId).unwrap();
    } catch (error) {
      // Revert on error
      setIsFollowing(!newState);
      console.error('Failed to toggle follow:', error);
    }
  };

  const handleListingPress = (listing: MarketplaceListing) => {
    // Use push to create a new screen instance instead of navigating back to existing one
    navigateToListing(listing, true);
  };

  // Get first name for listings section title
  const firstName = sellerProfile.name ? sellerProfile.name.split(' ')[0] : '';

  // Loading state
  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
      </View>
    );
  }

  return (
    <MarketplaceScreenLayout navigatorTitle="Seller profile">
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={[
          styles.content,
          { paddingHorizontal: contentPadding, paddingTop: contentPadding },
          !isMobile && styles.contentLarge,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Responsive content wrapper */}
        <View style={[
          styles.contentWrapper,
          maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
        ]}>
          {/* Seller Section */}
          <View style={styles.sellerSection}>
            {/* Back Navigation */}
            <BreadcrumbNavigation 
              onHomePress={handleGoToMarketplace}
              onBackPress={handleGoBack}
            />

            {/* Profile Info */}
            <View style={styles.profileSection}>
              {/* Avatar and Name Row */}
              <View style={styles.profileTop}>
                <View style={[
                  styles.avatar,
                  !isMobile && styles.avatarLarge,
                ]}>
                  <Text style={[
                    styles.avatarText,
                    !isMobile && styles.avatarTextLarge,
                  ]}>{sellerProfile.initials}</Text>
                </View>
                <View style={styles.profileInfo}>
                  <Text style={[
                    styles.sellerName,
                    !isMobile && styles.sellerNameLarge,
                  ]}>{sellerProfile.name}</Text>
                  <View style={styles.ratingRow}>
                    <StarRating rating={sellerProfile.rating} />
                    <Text style={styles.reviewCount}>({sellerProfile.reviewCount})</Text>
                  </View>
                </View>
              </View>

              {/* Stats Row */}
              <View style={[
                styles.statsRow,
                !isMobile && styles.statsRowLarge,
              ]}>
                <StatItem 
                  value={sellerProfile.bought} 
                  label="Bought" 
                  valueStyle={styles.statValue}
                  labelStyle={styles.statLabel}
                />
                <StatItem 
                  value={sellerProfile.sold} 
                  label="Sold" 
                  valueStyle={styles.statValue}
                  labelStyle={styles.statLabel}
                />
                <StatItem 
                  value={sellerProfile.followers} 
                  label="Followers" 
                  valueStyle={styles.statValue}
                  labelStyle={styles.statLabel}
                />
                <StatItem 
                  value={sellerProfile.following} 
                  label="Following" 
                  valueStyle={styles.statValue}
                  labelStyle={styles.statLabel}
                />
              </View>

              {/* Follow Button - only show when viewing someone else's profile */}
              {!isOwnProfile && (
                <TouchableOpacity 
                  style={[
                    styles.followButton,
                    isFollowing && styles.followingButton,
                    !isMobile && styles.followButtonLarge,
                  ]}
                  onPress={handleFollowPress}
                  activeOpacity={0.8}
                >
                  {isFollowing && (
                    <CheckIcon width={18} height={18} color={MARKETPLACE_COLORS.accentBlue} />
                  )}
                  <Text style={[
                    styles.followButtonText,
                    isFollowing && styles.followingButtonText,
                    !isMobile && styles.followButtonTextLarge,
                  ]}>
                    {isFollowing ? 'Following' : 'Follow'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Listings Section */}
          <View style={styles.listingsSection}>
            <Text style={[
              styles.listingsTitle,
              !isMobile && styles.listingsTitleLarge,
            ]}>{isOwnProfile ? 'Your listings' : `${firstName}'s listings`}</Text>
            <View style={[styles.listingsGrid, { gap: gridGap }]}>
              {sellerProfile.listings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  size={itemSize}
                  onPress={() => handleListingPress(listing)}
                />
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </MarketplaceScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.background,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 24,
  },

  // Seller Section
  sellerSection: {
    gap: 20,
  },

  // Profile Section
  profileSection: {
    gap: 20,
  },
  profileTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  avatar: {
    width: 68,
    height: 68,
    borderRadius: 34,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLarge: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  avatarText: {
    fontSize: 24,
    fontWeight: '700',
    color: MARKETPLACE_COLORS.textPrimary,
    textTransform: 'uppercase',
  },
  avatarTextLarge: {
    fontSize: 28,
  },
  profileInfo: {
    flex: 1,
    gap: 6,
  },
  sellerName: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 27,
  },
  sellerNameLarge: {
    fontSize: 22,
    lineHeight: 33,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
  },
  reviewCount: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },

  // Stats Row
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsRowLarge: {
    maxWidth: 400,
  },
  statValue: {
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 18,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
  },

  // Follow Button
  followButton: {
    height: 34,
    backgroundColor: MARKETPLACE_COLORS.accentBlueSolid,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
  },
  followButtonLarge: {
    height: 40,
    maxWidth: 200,
    borderRadius: 10,
  },
  followingButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  followButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  followButtonTextLarge: {
    fontSize: 14,
    lineHeight: 21,
  },
  followingButtonText: {
    color: MARKETPLACE_COLORS.accentBlue,
  },

  // Listings Section
  listingsSection: {
    gap: 16,
  },
  listingsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 27,
  },
  listingsTitleLarge: {
    fontSize: 20,
    lineHeight: 30,
  },
  listingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});
